import '@/index.css';
import { createInertiaApp } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';
import { initializeMixpanel } from '@/@configs/mixpanel';
import { initializeSentry } from '@/@configs/sentry';
import { AppShell } from '@/app-shell';
import { Layout } from '@/components/layout';
import { InertiaNavigationProvider } from '@/hooks/use-navigation/inertia-navigation';

initializeSentry();
initializeMixpanel();

type PageModule = {
  default: React.ComponentType;
};

const pages = import.meta.glob<PageModule>('../packages/**/*.tsx', {
  eager: true,
});

function resolvePage(name: string): PageModule {
  const key = `../packages/${name}.tsx`;
  const page = pages[key];

  if (!page) {
    throw new Error(`Page not found: ${key}`);
  }

  return page;
}

function wrapWithInertiaLayout(
  Page: React.ComponentType,
  auth?: boolean
): React.ComponentType {
  function InertiaPage(props: Record<string, unknown>) {
    return (
      <InertiaNavigationProvider>
        <Layout auth={auth}>
          <Page {...props} />
        </Layout>
      </InertiaNavigationProvider>
    );
  }

  InertiaPage.displayName = `InertiaPage(${Page.displayName || Page.name})`;

  return InertiaPage;
}

createInertiaApp({
  resolve: (name) => {
    const page = resolvePage(name);
    const spa = name === 'spa/index';
    const auth = name.startsWith('auth/');

    if (spa) return page;

    return { default: wrapWithInertiaLayout(page.default, auth) };
  },
  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        <AppShell>
          <App {...props} />
        </AppShell>
      );
    }
  },
});
